import { ReactElement, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { BreakpointMedia } from 'config';
import { capitalize } from '../../../utils/strings';
import MealTypes from '../../../models/MealTypes';
import { useCart } from '../../../contexts/Cart';
import Nav from '../../../models/Nav';
import ProgramRecipe from './ProgramRecipe';

type Props = {
    handleFilterClick: (newFilter: string) => void;
};

function ProgramRecipes({ handleFilterClick }: Props): ReactElement {
  const { cart } = useCart();
  const { program_menu_items: programMenuItems } = cart;
  const showExploreMenuButton = useMediaQuery({ query: BreakpointMedia.XL });

  const [selectedFilter, setSelectedFilter] = useState<string>('breakfast');

  const handleSubFilterClick = (filter: string) => {
    if (selectedFilter !== filter) {
      setSelectedFilter(filter);
    }
  };

  /* Returns up to 4 items to display for a category. We first prioritize the items that are
   * already in a customer's cart (which can include items that have sold out since being
   * added to the cart). If less than 4 items are present in the cart, we include the item(s)
   * with the next highest priority ranking that are not sold out. As a last resort, we will
   * show the next highest priority sold out item although it cannot be added to cart.
   */
  const getItemsToDisplay = (category: string) => {
    const items = programMenuItems[category];
    const itemsInCart = items.filter((item) => item.quantity > 0);
    // TODO: we should not have 2 different values for sold out
    const backupItems = items.filter((item) => item.quantity === 0
        && !item.sold_out && !item.isSoldOut);
    const unavailableItems = items.filter((item) => item.quantity === 0
        && (item.sold_out || item.isSoldOut));

    return [
      ...itemsInCart,
      ...backupItems,
      ...unavailableItems,
    ].slice(0, 4);
  };

  const categoryDisplays = Object.keys(programMenuItems)
    .filter((category) => category.toLowerCase() === selectedFilter.toLowerCase())
    .map((category) => {
      const itemsToDisplay = getItemsToDisplay(category);

      return (
        <div key={category} className="c-programs-cart-page__program-gallery">
          {itemsToDisplay.map((item) => (
            <ProgramRecipe key={item.sku.id} sku={item.sku} />
          ))}
        </div>
      );
    });

  return (
    <main className="c-programs-cart-page">
      <div className="c-programs-cart-page__header">
        <h1>{capitalize(MealTypes.programs.narrative)}</h1>
        {MealTypes.programs.description && (
        <div className="u-text u-text--md d-none d-sm-block">
          {MealTypes.programs.description}
        </div>
        )}
      </div>
      {showExploreMenuButton && (
        <button
          type="button"
          onClick={() => handleFilterClick('view_all')}
          className="c-programs-cart-page__explore-button"
        >
          Explore the Menu
        </button>
      )}
      <div className="c-programs-cart-page__filter-container">
        {Nav.programSubFilters.map((nav) => (
          <div
            role="button"
            tabIndex={0}
            key={nav.key}
            className={`c-programs-cart-page__filter ${selectedFilter.toLowerCase() === nav.key.toLowerCase() ? 'active' : ''}`}
            onClick={() => handleSubFilterClick(nav.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSubFilterClick(nav.key);
              }
            }}
          >
            {nav.webLabel}
          </div>
        ))}
      </div>
      <hr />
      {categoryDisplays}
    </main>
  );
}

export default ProgramRecipes;
